import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * The line item property used to flag a line as an automatically added free gift.
 * It starts with an underscore so it stays hidden from the customer at checkout.
 */
const GIFT_PROPERTY = '_free_gift';

/**
 * Module-level lock so that, when several `<free-shipping-bar>` elements exist on
 * the page (e.g. the cart drawer AND the cart page), only one reconcile runs at a
 * time. Reconcile is idempotent, so any follow-up run simply finds nothing to do.
 * @type {boolean}
 */
let reconciling = false;

/**
 * Custom element that keeps the cart's free-gift lines in sync with the configured
 * reward tiers. The visual progress bar itself is rendered server-side (Liquid) and
 * refreshes automatically through section morphing; this element only handles the
 * side effect of adding / removing the unlisted free gift products.
 */
class FreeShippingBar extends HTMLElement {
  /** @type {Array<{threshold: number, variantId: number}>} */
  #tiers = [];

  connectedCallback() {
    this.#tiers = this.#parseTiers();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    // Also re-evaluate when a discount changes the total (applying/removing a code or
    // automatic discount fires `discount:update`, not `cart:update`).
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);

    // Diagnostic: bar rendered but not one tier resolved a purchasable gift variant. Usually
    // means every assigned gift product is a Draft, deleted, or not published to the Online
    // Store sales channel (Liquid can't read its variant id), so nothing can be auto-added.
    if (!this.#tiers.length) {
      console.warn(
        '[free-shipping-bar] No gift variants to add. If you expect a free gift, check that the ' +
          'gift product is Active and published to the Online Store sales channel. Raw tiers:',
        this.getAttribute('data-gift-tiers')
      );
    }

    // Reconcile once on load in case the cart already qualifies (or no longer does).
    this.#reconcile();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.#onCartUpdate);
  }

  #onCartUpdate = () => {
    this.#reconcile();
  };


  /**
   * @returns {Array<{threshold: number, variantId: number}>}
   */
  #parseTiers() {
    try {
      const parsed = JSON.parse(this.getAttribute('data-gift-tiers') || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((tier) => tier && tier.variantId && tier.threshold >= 0);
    } catch {
      return [];
    }
  }

  /**
   * Reads the authoritative cart state and adds / removes free gift lines so that a
   * gift is present exactly when its tier threshold is met by the qualifying subtotal.
   */
  async #reconcile() {
    if (!this.#tiers.length || reconciling) return;
    reconciling = true;

    try {
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((response) => response.json());
      if (!cart || !Array.isArray(cart.items)) return;

      // Qualifying amount = the Estimated total the customer pays (cart.total_price, AFTER
      // all discounts), minus any auto-added free gift line value. This matches the cart's
      // "Estimated total", so a tier only counts as reached once the paid amount hits its
      // threshold — and a discount that pushes the total below a tier re-locks it.
      let qualifyingSubtotal = cart.total_price;
      /** @type {Array<{key: string, variant_id: number, quantity: number}>} */
      const giftLines = [];

      for (const item of cart.items) {
        const isGift = item.properties && item.properties[GIFT_PROPERTY];
        if (isGift) {
          giftLines.push(item);
          qualifyingSubtotal -= item.final_line_price;
        }
      }

      // Which gift variants *should* be in the cart right now.
      const desired = new Set(
        this.#tiers.filter((tier) => qualifyingSubtotal >= tier.threshold).map((tier) => tier.variantId)
      );

      // Work out the removals / quantity fixes for gift lines already present.
      /** @type {Array<{key: string, quantity: number}>} */
      const lineChanges = [];
      const present = new Set();

      for (const line of giftLines) {
        const alreadyHandled = present.has(line.variant_id);
        if (!desired.has(line.variant_id) || alreadyHandled) {
          // No longer qualified, not a configured gift, or a duplicate line: remove it.
          lineChanges.push({ key: line.key, quantity: 0 });
        } else {
          present.add(line.variant_id);
          if (line.quantity !== 1) lineChanges.push({ key: line.key, quantity: 1 });
        }
      }

      const variantsToAdd = [...desired].filter((variantId) => !present.has(variantId));

      if (!lineChanges.length && !variantsToAdd.length) return;

      // Apply changes to existing lines first, then add any missing gifts.
      for (const change of lineChanges) {
        await this.#changeLine(change.key, change.quantity);
      }
      if (variantsToAdd.length) {
        await this.#addGifts(variantsToAdd);
      }

      await this.#refreshCartSections();
    } catch (error) {
      console.error('[free-shipping-bar] reconcile failed', error);
    } finally {
      reconciling = false;
    }
  }

  /**
   * @param {number[]} variantIds
   */
  async #addGifts(variantIds) {
    const items = variantIds.map((id) => ({
      id,
      quantity: 1,
      properties: { [GIFT_PROPERTY]: 'true' },
    }));

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body: JSON.stringify({ items }) }));

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      console.error(
        `[free-shipping-bar] Could not add free gift (${response.status}). ` +
          'Check that the gift product is Active, published to the Online Store sales channel, ' +
          'and in stock (or set to keep selling when out of stock).',
        error
      );
    }
  }

  /**
   * @param {string} key - The cart line key.
   * @param {number} quantity - The new quantity (0 removes the line).
   */
  async #changeLine(key, quantity) {
    await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body: JSON.stringify({ id: key, quantity }) }));
  }

  /**
   * Re-renders every cart section on the page (drawer + cart page) without dispatching
   * a `cart:update` event, so we neither auto-open the drawer nor re-trigger reconcile.
   */
  async #refreshCartSections() {
    const sectionIds = new Set();
    for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
      const id = component.getAttribute('data-section-id');
      if (id) sectionIds.add(id);
    }

    await Promise.all(
      [...sectionIds].map((id) => sectionRenderer.renderSection(id, { cache: false }).catch(() => {}))
    );
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}